import React, {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import * as css from './PromotionSection.css';
import PromotionMessage from '../../promotionMessage/PromotionMessage';
import PromotionPopupContent from '../../managePromotion/PromotionPopupContent';
import PromotionWhatsappButton from '../../managePromotion/PromotionWhatsappButton';
import {getStaticImage} from '../../../globalVariables/GlobalVariables';
import PromotionPopup from '../../prompotionPopup/PromotionPopup';
import {TShow} from '../../../hooks/useShow';
import {acceptPromotion, promotionAnalytics} from '../../../redux/event/eventActions';
import {logGoogleEvent} from '../../../utils/analytics';

const PromotionSection = ({showPromotion, showPromotionPopup, colors} : {showPromotion: TShow; showPromotionPopup: TShow; colors: any;}) => {
    const {t} = useTranslation('promotionMessage');
    const termsApproved = useSelector((state: any) => state.event.promotionMessage?.termsApproved);
    const pmid = useSelector((state: any) => state.event.promotionMessage?.pmid);
    const backgroundColor = `linear-gradient(180deg, ${colors[0] || ''} 0%, ${colors[1] || ''} 100%);`;
    const backgroundImage = getStaticImage('app/promotion/oneWeekBeforeEventPromotion.png');
    const PUBLISH_EVENT_PROMOTION = 5;

    const messageClickLogged = useRef(false);
    const popupClickLogged = useRef(false);
    const viewLogged = useRef(false);

    const logAnalytics = (eventName: string, params?: Record<string, unknown>) => {
        // attribute to the message actually loaded - the banner can also show a stale
        // non-publish message left in redux, and a seen message is always reported
        logGoogleEvent(`publish_${eventName}`, pmid, `pmid-${pmid}`, {
            popup_name: 'promotionMessage',
            feature: 'publish',
            ...params
        });
    };

    // buttons stay clickable during the 300ms close animation - log one click per open
    const logClickOnce = (logged: React.MutableRefObject<boolean>, eventName: string, params?: Record<string, unknown>) => {
        if(logged.current)
            return;
        logged.current = true;
        logAnalytics(eventName, params);
    };

    const handleAccept = () => {
        logClickOnce(messageClickLogged, 'promotion_message_accept_click', {button_name: 'accept'});
        promotionAnalytics(PUBLISH_EVENT_PROMOTION, 'send_click').then(() => {
            showPromotion.close();
            showPromotionPopup.open();
        });
        acceptPromotion(PUBLISH_EVENT_PROMOTION);
    };

    const handleClose = (e?: Event | React.SyntheticEvent) => {
        logClickOnce(messageClickLogged, 'promotion_message_close_click', {button_name: 'close'});
        return showPromotion.close(e);
    };

    const closePopup = (e?: Event | React.SyntheticEvent) => {
        logClickOnce(popupClickLogged, 'promotion_popup_close_click', {button_name: 'close', popup_name: 'promotionPopup'});
        return showPromotionPopup.close(e);
    };

    const thanksClick = () => {
        logClickOnce(popupClickLogged, 'promotion_popup_thanks_click', {button_name: 'thanks', popup_name: 'promotionPopup'});
        showPromotionPopup.close();
    };

    useEffect(() => {
        if(showPromotion.isOpen && pmid && !viewLogged.current) {
            viewLogged.current = true;
            messageClickLogged.current = false;
            logAnalytics('promotion_message_view');
        }
    }, [showPromotion.isOpen, pmid]);

    useEffect(() => {
        if(showPromotionPopup.isOpen) {
            popupClickLogged.current = false;
            logAnalytics('promotion_popup_open', {popup_name: 'promotionPopup'});
        }
    }, [showPromotionPopup.isOpen]);

    return (
        <>
            {showPromotion.isOpen && (
                <css.promotionMessage>
                    <PromotionMessage
                        show={showPromotion}
                        secondTitle={t('promotionMessageTitle')}
                        text={t('promotionMessageText')}
                        background={{image: backgroundImage, color: backgroundColor}}
                        button={{onClick: handleAccept, text: t('getHelp'), color: colors[2]}}
                        close={handleClose}
                        firstTitle={''}
                    />
                </css.promotionMessage>
            )}
            {showPromotionPopup.isOpen && (
                <css.promotionPopup>
                    <PromotionPopup show={{...showPromotionPopup, close: closePopup}} color={backgroundColor} image={backgroundImage} button={{onClick: thanksClick, text: t('thanks'), disabled: termsApproved != 1}} extraButton={<PromotionWhatsappButton pmid={PUBLISH_EVENT_PROMOTION} feature="publish" />}>
                        <PromotionPopupContent pmid={PUBLISH_EVENT_PROMOTION} feature="publish" />
                    </PromotionPopup>
                </css.promotionPopup>
            )}
        </>
    );
};

export default PromotionSection;